/* @HEADER
 * @version 0.15.943 | 2026-07-15
 * @purpose Accessible in-app confirmation modal shown before moving a project to a different organization, replacing the native window.confirm previously used by ProjectSwitcher (ZVX-DEV-2692).
 * @sidecar ProjectOrgConfirmDialog.tsx.header.md
 * @layer app | @hex adapter | @ctx project
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-2692
//
// Modeled on features/okr/components/OkrConfirmDialog.tsx's proven a11y
// pattern (portal render, role="alertdialog", two-button focus trap,
// Esc/overlay cancel, return-focus on close) — that component is not
// exported from the OKR public API for cross-feature reuse, so this is a
// narrow adaptation scoped to the project-switcher org-move flow, following
// the same precedent as features/settings/license/LicenseRebindConfirmDialog.tsx.

import { useCallback, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { PROJECT_SWITCHER_TESTIDS } from './project-switcher.testids'

export interface ProjectOrgConfirmDialogProps {
  open: boolean
  title: string
  body: string
  confirmLabel: string
  cancelLabel: string
  onConfirm: () => void
  onCancel: () => void
}

export function ProjectOrgConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: ProjectOrgConfirmDialogProps) {
  const titleId = useId()
  const bodyId = useId()

  const cancelBtnRef = useRef<HTMLButtonElement | null>(null)
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    returnFocusRef.current =
      typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null
    const id = window.setTimeout(() => {
      cancelBtnRef.current?.focus()
    }, 0)
    return () => {
      window.clearTimeout(id)
      const target = returnFocusRef.current
      if (target && typeof target.focus === 'function') {
        try {
          target.focus()
        } catch {
          // Trigger may have been torn down — nothing safe to do here.
        }
      }
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault()
        onCancel()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onCancel])

  const handleTrapKeyDown = useCallback((ev: React.KeyboardEvent<HTMLDivElement>) => {
    if (ev.key !== 'Tab') return
    const cancel = cancelBtnRef.current
    const confirm = confirmBtnRef.current
    if (!cancel || !confirm) return
    const active = typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null
    if (ev.shiftKey) {
      if (active === cancel) {
        ev.preventDefault()
        confirm.focus()
      }
    } else if (active === confirm) {
      ev.preventDefault()
      cancel.focus()
    }
  }, [])

  const handleOverlayMouseDown = useCallback(
    (ev: React.MouseEvent<HTMLDivElement>) => {
      if (ev.target === ev.currentTarget) onCancel()
    },
    [onCancel],
  )

  if (!open) return null

  const dialog = (
    <div
      className="project-org-confirm-dialog-overlay"
      data-testid={PROJECT_SWITCHER_TESTIDS.orgConfirmDialogOverlay}
      onMouseDown={handleOverlayMouseDown}
    >
      <div
        className="project-org-confirm-dialog"
        data-testid={PROJECT_SWITCHER_TESTIDS.orgConfirmDialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={handleTrapKeyDown}
      >
        <h2 id={titleId} className="project-org-confirm-dialog__title" data-testid={PROJECT_SWITCHER_TESTIDS.orgConfirmDialogTitle}>
          {title}
        </h2>
        <p id={bodyId} className="project-org-confirm-dialog__body" data-testid={PROJECT_SWITCHER_TESTIDS.orgConfirmDialogBody}>
          {body}
        </p>
        <div className="project-org-confirm-dialog__actions">
          <button
            ref={cancelBtnRef}
            type="button"
            className="project-org-confirm-dialog__btn project-org-confirm-dialog__btn--cancel"
            data-testid={PROJECT_SWITCHER_TESTIDS.orgConfirmDialogCancelBtn}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            className="project-org-confirm-dialog__btn project-org-confirm-dialog__btn--confirm"
            data-testid={PROJECT_SWITCHER_TESTIDS.orgConfirmDialogConfirmBtn}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return dialog
  return createPortal(dialog, document.body)
}
